import { z } from 'zod/v4';

import type { InfinexMessageNamespace } from './base';
import {
  targetTypeSchema, // runtime schema for the TargetType union
} from './base';

import { errorEnvelopeSchema } from '../errors';

/**
 * Build Zod schemas for an RPC‑style message family (request, response, notification)
 * without pre‑enumerating channel ↔ popup ↔ sdk ↔ server routes.
 *
 * Any TargetType can now appear in the {@link to}, {@link from} or {@link target} fields.
 */
export function generateEventSchema<
  TNamespace extends InfinexMessageNamespace,
  TMethod extends string,
  TRequestData extends z.ZodTypeAny,
  TResponseData extends z.ZodTypeAny,
  TNotificationData extends z.ZodTypeAny,
>(options: {
  namespace: TNamespace;
  method: TMethod;

  requestData: TRequestData;
  responseData: TResponseData;
  notificationData: TNotificationData;
}) {
  const { namespace, method, requestData, responseData, notificationData } =
    options;

  /* ---------- Core message shapes ---------- */

  // 1. request  ---------------------------------------------------------------
  const requestSchema = z.object({
    type: z.literal(`${namespace}:request`),
    to: targetTypeSchema,
    from: targetTypeSchema,
    method: z.literal(method),
    requestId: z.string(),
    data: requestData,
  });

  const requestParamsSchema = z.object({
    target: targetTypeSchema,
    namespace: z.literal(namespace),
    method: z.literal(method),
    data: requestData,
    timeoutMs: z.number().optional(),
  });

  // 2. response  --------------------------------------------------------------
  const responseSchema = z.object({
    type: z.literal(`${namespace}:response`),
    to: targetTypeSchema,
    from: targetTypeSchema,
    method: z.literal(method),
    requestId: z.string(),
    response: responseData.nullable(), // null when `error` is set
    error: errorEnvelopeSchema.optional(),
  });

  const responseParamsSchema = z.object({
    target: targetTypeSchema,
    namespace: z.literal(namespace),
    requestId: z.string(),
    method: z.literal(method),
    response: responseData.nullable(),
    error: z.unknown().optional(),
  });

  // 3. notification  ----------------------------------------------------------
  const notificationSchema = z.object({
    type: z.literal(`${namespace}:notification`),
    to: targetTypeSchema,
    from: targetTypeSchema,
    method: z.literal(method),
    timestamp: z.string(),
    data: notificationData,
  });

  const notificationParamsSchema = z.object({
    target: targetTypeSchema,
    namespace: z.literal(namespace),
    method: z.literal(method),
    data: notificationData,
  });

  /* ---------- Public contract ---------- */

  return {
    requestSchema,
    requestParamsSchema,
    responseSchema,
    responseParamsSchema,
    notificationSchema,
    notificationParamsSchema,
  };
}
